import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Label } from '@art-of-hospitality-solutions-llc/ui-kit/components/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import {
  toastSuccess,
  toastError,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import service from '@Service';
import type { OperationActionTriggerProps } from '@Constants/operations';
import { weddingReportKeys } from '@Hooks/useWeddingReports';
import { useWeddingReportRunStore } from './weddingReportRunStore';

// Local calendar date, not UTC — toISOString() rolls over to tomorrow once the
// local clock passes UTC midnight, which would put a future date in the picker.
const toIsoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function RunWeddingReportTrigger({
  soulId,
  onSuccess,
}: OperationActionTriggerProps) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const queryClient = useQueryClient();

  // The seven full days before today, plus today. Ending at today picks up
  // anything won so far; starting a week back keeps all seven complete days
  // rather than dropping one to make room for it.
  const defaultEnd = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const d = new Date(defaultEnd);
    d.setDate(d.getDate() - 7);
    return d;
  }, [defaultEnd]);

  const [startDate, setStartDate] = useState(toIsoDate(defaultStart));
  const [endDate, setEndDate] = useState(toIsoDate(defaultEnd));
  // Off by default — the scheduled run is what normally mails the team.
  const [sendEmail, setSendEmail] = useState(false);

  const handleRun = async () => {
    if (!startDate || !endDate) {
      toastError('Pick a start and end date.');
      return;
    }
    if (endDate < startDate) {
      toastError('End date must be on or after start date.');
      return;
    }
    setRunning(true);
    // Show the "thinking" thought while the (synchronous) job runs…
    useWeddingReportRunStore.getState().begin(soulId);
    setOpen(false);
    try {
      const res = await service.runWeddingRollup({
        startDate,
        endDate,
        sendEmail,
      });
      const found = res?.data?.bookingsFound ?? 0;
      const emailed = res?.data?.contactsEmailed ?? 0;
      queryClient.invalidateQueries({
        queryKey: weddingReportKeys.forSoul(soulId),
      });
      toastSuccess(
        found > 0
          ? `Found ${found} wedding${found === 1 ? '' : 's'} won in that range.` +
              (sendEmail
                ? ` Emailed ${emailed} contact${emailed === 1 ? '' : 's'}.`
                : '')
          : 'No weddings went definite in that range.',
      );
      onSuccess();
    } catch {
      toastError('Failed to run the wedding rollup.');
    } finally {
      // …and stop it deterministically, rather than waiting for a new report that
      // may be identical to the one it replaced.
      useWeddingReportRunStore.getState().end(soulId);
      setRunning(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={running}>
          {running ? 'Running…' : 'Run Wedding Rollup'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="flex flex-col space-y-3">
          <div>
            <Heading level="h4">Wedding Rollup Range</Heading>
            <p className="mt-1 text-xs text-zinc-500">
              Weddings that went definite in this range. Each run adds a new
              digest to the feed.
            </p>
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="desk-wedding-start">Start Date</Label>
            <Input
              id="desk-wedding-start"
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="desk-wedding-end">End Date</Label>
            <Input
              id="desk-wedding-end"
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <label className="flex cursor-pointer items-start gap-2 pt-1">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-xs text-zinc-600">
              Email the agent's contacts
              <span className="block text-zinc-400">
                Off by default so test runs stay quiet.
              </span>
            </span>
          </label>
          <div className="flex justify-end space-x-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={running}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleRun} loading={running}>
              {running ? 'Running…' : 'Run'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
